import { Trans } from "../utils/i18n";
import React from "react";
import {validateEmailRegEx, validatePhoneNumber, validateVinRegEx} from "../Shared/FormField";
import DeviceWidget from "../FormComponents/DeviceWidget/DeviceWidget";
import {isArray} from "lodash";
import {array as memoizedValidation} from "../Shared/FormField";
import dayjs from "dayjs";
import { usePage } from "@inertiajs/inertia-react";
import Markdown from "react-markdown";
import {addressConfigWithInvoice} from "./components/address";
import EmailValidation from "../FormComponents/EmailValidation";

const validProblem = (values, orderEntryState) => {
    const serialNumber = orderEntryState && orderEntryState.serialNumber;

    const currentProblem = serialNumber && serialNumber.product?.problems.find(item => parseInt(values.problem_1) === item.id);
    const currentProblem2 = currentProblem && currentProblem.problems?.find(item => parseInt(values.problem_2) === item.id);

    return values.problem_1 && values.problem_2 && !currentProblem2?.education_text;
};

export const hasProducts = (products) => {
    // In the case of array fields (e. g. in SelfRepairProductSelect or SelfRepairCheckout)
    // products is not an object like ({ [id]: [amount] }) but an array of products like [{id, amount}, ...]
    if (isArray(products)) {
        return products.filter(item => item && Number(item.amount ?? 0) > 0).length > 0;
    }

    return products && Object.values(products).filter(item => item && item !== "0").length > 0;
}

export const orderEntryConfigSegway = {
    "sizes": {
        "forth": 3,
        "third": 4,
        "half": 6,
        "full": 12
    },
    "enableStepper": true,
    "heading": <Trans i18nKey="orderEntry.basicData">Basic data</Trans>,
    "defaultValues": {
        "private_or_company": "private",
    },
    "groups": [
        {
            "heading": <Trans i18nKey="orderEntry.productSelection">Product selection</Trans>,
            "description": <Trans i18nKey="orderEntry.productSelection.description"></Trans>,
            "id": "product_selection",
            "fields": [
                {
                    "size": "full",
                    "component": "requesttypeselect"
                },
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values) => <>
                        <div className={'flex justify-center items-center w-full mt-6'}>
                            <div className={"py-6 px-9 rounded-2xl shadow-lg min-w-lg text-center bg-red-100"}>
                                <h3 className={'h3 text-center'}>
                                    Leider können wir für Deutschland aktuell noch keinen Service anbieten. Sollten Sie ein B2B Kunde sein, loggen Sie sich bitte unter folgendem Link ein um eine Reparatur anzumelden. Vielen Dank.
                                </h3>
                                <a className={'underline text-black mt-1.5 text-sm'} href={'https://segway.one-connect.b2x.com/login'} target={'_blank'}>
                                    https://segway.one-connect.b2x.com/login
                                </a>
                            </div>
                        </div>
                    </>,
                    "conditionFunction": (values, orderEntryState) => {
                        const { currentCountry, client } = usePage().props;
                        return currentCountry === 'DE' && client.key === 'segway';
                    },
                },
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values) => <>
                        <h3 className={'mt-9'} style={{ marginBottom: 0 }}><Trans i18nKey={'orderEntry.model.pleaseSelect'}>Please select your model</Trans></h3>
                    </>,
                    "conditionFunction": (values, orderEntryState) => {
                        const { currentCountry, client } = usePage().props;
                        return currentCountry !== 'DE' || client.key === 'segway-de';
                    },
                },
                {
                    "size": "full",
                    "component": "productconfig",
                    "disableColorSelection": true,
                    "columns": 4,
                    "conditionFunction": (values, orderEntryState) => {
                        const { currentCountry, client } = usePage().props;
                        return currentCountry !== 'DE' || client.key === 'segway-de';
                    },
                },
            ],
        },
        {
            "heading": <Trans i18nKey="orderEntry.warrantyCheck">Warranty Check</Trans>,
            "description": <Trans i18nKey="orderEntry.productSelection.description"></Trans>,
            "id": "warrantyCheck",
            "fields": [
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values, orderEntryState) => {
                        return <><DeviceWidget hidePrice hideBadge values={values}  />
                    </>},
                },
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values) => <>
                        <h3><Trans i18nKey={'orderEntry.upload-receipt-only'}>Upload receipt</Trans></h3>
                        <p><Trans i18nKey={'orderEntry.upload-receipt-only.description'}>Please upload your invoice or receipt. You can upload JPG, PNG or PDF files up to 20 MB size.</Trans></p>
                    </>,
                    "conditionFunction": (values, orderEntryState) => {
                        return orderEntryState?.selectedProduct;
                    },
                },
                {
                    "size": "full",
                    "component": "dropzoneform",
                    "type": "receipt",
                    "conditionFunction": (values, orderEntryState) => {
                        return orderEntryState?.selectedProduct;
                    },
                },
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values) => <>
                        <h3 className={'mt-6'}><Trans i18nKey={'orderEntry.enterPopDate'}>Enter the date of purchase</Trans></h3>
                    </>,
                    "conditionFunction": (values, orderEntryState) => {
                        return orderEntryState?.selectedProduct && !!orderEntryState?.files?.length;
                    },
                },
                {
                    "name": "pop_date",
                    "label": <Trans i18nKey="orderEntry.pop_date">Date of purchase</Trans>,
                    "size": "full",
                    "type": "datepicker",
                    "required": true,
                    "allowPast": true,
                    "allowToolbar": true,
                    "maxDate": dayjs().toISOString(),
                    "conditionFunction": (values, orderEntryState) => {
                        return orderEntryState?.selectedProduct && !!orderEntryState?.files?.length;
                    },
                    "onChangeCallback": (values, orderEntryDispatch) => {
                        orderEntryDispatch({ type: 'setSerialNumber', value: {} });
                    },
                },
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values) => <>
                        <h3 className={'mt-6'}><Trans i18nKey={'orderEntry.enterSerialNumber'}>Enter the serial number of your product</Trans></h3>
                    </>,
                    "conditionFunction": (values, orderEntryState) => {
                        return orderEntryState?.selectedProduct && !!orderEntryState?.files?.length && values?.pop_date;
                    },
                },
                {
                    "component": "serialnumber",
                    "size": "full",
                    "required": true,
                    "uppercase": true,
                    "doNotShowConfirmDevice": true,
                    "conditionFunction": (values, orderEntryState) => {
                        return orderEntryState?.selectedProduct && !!orderEntryState?.files?.length && values?.pop_date;
                    },
                },
                {
                    "size": "full",
                    "component": "callback",
                    "conditionFunction": (values, orderEntryState) => orderEntryState?.selectedProduct && !!orderEntryState?.files?.length && values?.pop_date && orderEntryState.serialNumber?.success,
                    "callback": (values, orderEntryState) => {
                        const product = orderEntryState.selectedProduct;
                        return <><DeviceWidget  values={values}  style="alternative"/>
                    </>},
                },
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values, orderEntryState) => {
                        const { currentCountry } = usePage().props;
                        if (currentCountry == 'RS' || currentCountry == 'BA' || currentCountry == 'ME') {
                            return <>
                                <p className='bg-white gap-3 rounded-lg p-6 b2x-shadow-md border border-2 border-secondary-900'>
                                Prikazana početna naknada za popravku se odnosi na dijagnostiku, kartonsku kutiju (ako je potrebno) i usluge transporta i uključuje otpremu, porez i rukovanje. Ovaj iznos može da varira u zavisnosti od zemlje u kojoj će se izvršiti popravka. Kada primimo i potvrdimo problem sa Vašim uređajem, na Vašu e-poštu ćemo potvrditi dodatnu naknadu za popravku uređaja da bismo rešili ovaj problem. Od Vas će se tražiti da odobrite i platite ovaj trošak pre nego što nastavimo sa popravkom Vašeg uređaja.
                                </p>
                                </>;
                        }
                        if (currentCountry == 'MK') {
                            return <>
                                <p className='bg-white gap-3 rounded-lg p-6 b2x-shadow-md border border-2 border-secondary-900'>
                                Прикажаната почетна цена е за дијагностика, картонска кутија (ако е потребно) и транспортни услуги и вклучува испорака, данок и манипулативни трошоци. Оваа сума може да варира во зависност од земјата каде што ќе се врши услугата за поправка. Откако ќе го примиме уредот и го потврдиме проблемот со Вашиот уред, на Вашата е-пошта ќе ја потврдиме почетната цена за поправка на уредот за да го решиме овој проблем. Од Вас ќе биде побарано да ја одобрите и платите оваа цена пред да продолжиме со поправка на Вашиот уред.
                                </p>
                                </>;
                        }

                        return <>
                            <p className='bg-white gap-3 rounded-lg p-6 b2x-shadow-md border border-2 border-secondary-900'>
                                <Trans
                                i18nKey={'orderEntry.price.disclaimer@niu'}
                            >
                                * Initial Repair Fee shown is for the diagnostics, carton box (if required) and transport services and includes shipping, tax, and handling. This amount may vary depending on the country where the repair service will be performed. Once we have received and confirmed the issue with your device we will confirm to your e-mail the additional Device Repair Fee to fix this issue.  You will be requested to approve and pay this cost before we proceed to repair your device.
                            </Trans>
                            </p>
                        </>;
                    },
                    "conditionFunction": (values, orderEntryState) => {
                        const { currentCountry } = usePage().props;
                        return orderEntryState?.selectedProduct && !!orderEntryState?.files?.length && values?.pop_date && orderEntryState.serialNumber?.success && orderEntryState.serialNumber?.warranty === false && currentCountry != 'ES' && currentCountry != 'PT';
                    }
                },
            ],
            "conditionFunction": (values, orderEntryState) => {
                return values.requesttype && orderEntryState?.selectedProduct;
            },
        },
        {
            "heading": <Trans i18nKey="orderEntry.problemDescription">Problem description</Trans>,
            "description": <Trans i18nKey="orderEntry.problemDescription.description">Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua.</Trans>,
            "id": "problem_description",
            "fields": [
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values, orderEntryState) => {
                        return <><DeviceWidget values={values}  />
                    </>},
                },
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values) => <>
                        <h2 style={{ marginBottom: 0}}><Trans i18nKey={'orderEntry.problem.heading'}>What happend?</Trans></h2>
                        <p><Trans i18nKey="orderEntry.problemDescription.description">Tell us about the problem. Select a category to continue</Trans></p>
                    </>,
                },
                {
                    "size": "full",
                    "component": "problemselectbig",
                    "hideProblemInput": true,
                },
            ],
            "conditionFunction": (values, orderEntryState) => {
                return values.requesttype && orderEntryState?.selectedProduct && orderEntryState.serialNumber?.success  && !!values?.pop_date;
            },
        },
        {
            "heading": <Trans i18nKey={'orderEntry.problemImage'}>Images of defects</Trans>,
            "description": <Trans i18nKey="orderEntry.problemDescription.description">Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua.</Trans>,
            "id": "image_upload",
            "fields": [
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values) => <>
                        <h3><Trans i18nKey={'orderEntry.problemImage'}>Images of defects</Trans></h3>
                        <p><Trans i18nKey={'orderEntry.problemImage.description'}>Please upload pictures showing the defects where possible. You can upload JPG, PNG or PDF files up to 20 MB size.</Trans></p>
                    </>,
                    "conditionFunction": (values, orderEntryState) => {
                        return validProblem(values, orderEntryState);
                    },
                },
                {
                    "size": "full",
                    "component": "dropzoneform",
                    "type": "problem",
                    "conditionFunction": (values, orderEntryState) => {
                        return validProblem(values, orderEntryState);
                    }
                },
                {
                    "size": "full",
                    "name": "problem_description",
                    "label": <Trans i18nKey="serialNumber.problemDescription">Problem description</Trans>,
                    "multiline": true,
                    "validatetype": "max200chars",
                    "conditionFunction": (values, orderEntryState) => {
                        return validProblem(values, orderEntryState);
                    }
                }
            ],
            "conditionFunction": (values, orderEntryState) => {
                return validProblem(values, orderEntryState) && values.requesttype && orderEntryState?.selectedProduct && orderEntryState.serialNumber?.success  && !!values?.pop_date;
            },
        },
        {
            "heading": <Trans i18nKey="orderEntry.yourDetails">Your Details</Trans>,
            "description": <Trans i18nKey="orderEntry.addressContact.description">Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua.</Trans>,
            "id": "address",
            "fields": [
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values, orderEntryState) => {
                        return <><DeviceWidget values={values}  />
                        </>},
                },
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values) => <>
                        <h2><Trans i18nKey={'orderEntry.contactInfo'}>Contact info</Trans></h2>
                    </>,
                },
                ...addressConfigWithInvoice,
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values, orderEntryState) => {
                        return <EmailValidation values={values} />;
                    },
                },
            ],
            "conditionFunction": (values, orderEntryState) => {
                const product = orderEntryState.selectedProduct;

                if(values.requesttype === 'rma') {
                    return ( values.requesttype && product && validProblem(values, orderEntryState) && orderEntryState.serialNumber?.success );
                }

                return values.requesttype && product && validProblem(values, orderEntryState) && orderEntryState.serialNumber?.success && hasProducts(values.products);
            },
        },
        {
            "heading": <Trans i18nKey="common.checkout">Confirmation / Summary</Trans>,
            "description": <Trans i18nKey="orderEntry.termsandconditions.description"></Trans>,
            "id": "terms",
            "fields": [
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values, orderEntryState) => {
                        return <><DeviceWidget values={values}  />
                    </>},
                },
                {
                    "size": "full",
                    "component": "callback",
                    "callback": (values) => <>
                        <h2 style={{ marginBottom: 0}}><Trans i18nKey={'orderEntry.verifyAccountToPay'}>Verify your information</Trans></h2>
                    </>,
                },
                {
                    "size": "full",
                    "component": "verify_phone_number"
                },
                {
                    "name": "terms_of_service",
                    "label": <Trans i18nKey="orderEntry.termsandconditions.label">I have read and accept the <a href={route('termsandconditions')} target={'_blank'}>Terms & Conditions</a> and the <a href={route('privacy')} target={'_blank'}>Privacy Statement</a></Trans>,
                    "size": "full",
                    "type": "checkbox",
                    "required": true
                },
                {
                    "component": "emptyboxcheckbox",
                    "name": "send_empty_box",
                },
            ],
            "conditionFunction": (values, orderEntryState) => {
                const validName = values?.private_or_company === "private" ? (values?.first_name && values?.last_name) : values?.name;
                const validAddress = validName && values.city && values.street && values.housenumber && values.zip && values.country_id && validatePhoneNumber.test(values.phone) && validateEmailRegEx.test(values.email);
                const validInvoiceAddress = values.deviating_invoice_address == true ? (values?.invoice_private_or_company === "private" ? (values?.invoice_first_name && values?.invoice_last_name) : values?.invoice_name) && values.invoice_city && values.invoice_street && values.invoice_zip && values.invoice_country_id : true;
                const product = orderEntryState.selectedProduct;

                return validProblem(values, orderEntryState) && product && orderEntryState.serialNumber?.success && validAddress && validInvoiceAddress && (values.requesttype === 'self_repair' && hasProducts(values.products) || values.requesttype === 'rma');
            },
        }
    ],
    "actions": [
        {
            "fields": [
                {
                    "text": <Trans i18nKey="orderEntry.send">Send</Trans>,
                    "id": "confirmsend",
                    "size": "half",
                    "component": "button",
                    "action": "next",
                    "color": "secondary",
                    "conditionFunction": (values, orderEntryState) => {
                        const validName = values?.private_or_company === "private" ? (values?.first_name && values?.last_name) : values?.name;
                        const validAddress = validName && values.city && values.street && values.housenumber && values.zip && values.country_id && validatePhoneNumber.test(values.phone) && validateEmailRegEx.test(values.email);
                        const product = orderEntryState.selectedProduct;

                        return ( validProblem(values, orderEntryState) && product && orderEntryState.serialNumber?.success && values.terms_of_service && orderEntryState?.verificationCodeResponse )
                                && !!validAddress;
                    }
                }
            ]
        }
    ]
}
